const MONTHS_MARKER = " - Months ";
const CUSTOM_OTHER_PREFIX = "Other Fees - Custom ";
const PURCHASE_PREFIX = "Other Fees - Purchase ";
const MONTH_PREFIX = "Month ";

// Term-option helpers for the Student Fee Payment dialog. Selection keys are
// `${fee_head_id}:${term}` and values are "full" or "installment_<n>".

/** Period labels already invoiced for this student, per fee head. */
export function chargedLabelsFromDues(dues) {
  const map = {};
  for (const row of dues || []) {
    for (const item of row.items || []) {
      if (item.status && item.status !== "scheduled" && item.period_label) {
        (map[row.fee_head_id] = map[row.fee_head_id] || new Set()).add(item.period_label);
      }
    }
  }
  return map;
}

/** True when the option would charge a period that is already invoiced. */
export function isOptionCharged(chargedLabels, line, term, optionId) {
  const labels = chargedLabels[line.fee_head_id];
  if (!labels) return false;
  const per = line.installmentsPerTerm || 1;
  const installmentLabel = (n) => `Term ${term} - Installment ${n} of ${per}`;
  if (labels.has(`Term ${term}`)) return true;
  // Custom months of this term rule out its full term and instalments.
  if ([...labels].some((label) => label.startsWith(`Term ${term}${MONTHS_MARKER}`))) return true;
  if (optionId === "full") {
    for (let n = 1; n <= per; n++) if (labels.has(installmentLabel(n))) return true;
    return false;
  }
  return labels.has(installmentLabel(parseInt(optionId.split("_")[1], 10)));
}

/** Term dues that have fallen due by `asOf`, as dialog selections. */
export function overdueTermSelections(dues, lines, asOf, chargedLabels) {
  const next = {};
  for (const row of dues || []) {
    if (!row.term_key) continue;
    const line = (lines || []).find(
      (l) => l.fee_head_id === row.fee_head_id && l.frequency === "term" && l.enabled !== false,
    );
    const overdue = (row.items || []).filter((i) => i.status === "scheduled" && i.due_date && i.due_date <= asOf);
    if (!line || !overdue.length) continue;
    const per = line.installmentsPerTerm || 1;
    const term = parseInt(row.term_key.replace("term", ""), 10);
    const option = per <= 1 || overdue.length >= per ? "full" : `installment_${overdue[0].installment_no}`;
    if (!isOptionCharged(chargedLabels, line, term, option)) next[`${line.fee_head_id}:${term}`] = option;
  }
  return next;
}

/** Drops selections whose option has since been charged. */
export function dropChargedSelections(selections, lines, chargedLabels) {
  const next = {};
  for (const [key, optionId] of Object.entries(selections || {})) {
    const [feeHeadId, termStr] = key.split(":");
    const line = (lines || []).find((l) => l.fee_head_id === feeHeadId);
    if (line && isOptionCharged(chargedLabels, line, parseInt(termStr, 10), optionId)) continue;
    next[key] = optionId;
  }
  return next;
}

/** Terms the dialog offers for a line: none before the plan's start term. */
export function chargeableTerms(line) {
  const start = parseInt(String(line?.effective_from_term || "").replace("term", ""), 10);
  return [1, 2, 3].filter((term) => !(start >= 1 && term < start));
}

/** Months of a term already charged for a fee head (custom month charges). */
export function chargedMonths(dues, feeHeadId, termKey) {
  const months = new Set();
  for (const row of dues || []) {
    if (row.fee_head_id !== feeHeadId || row.term_key !== termKey) continue;
    for (const item of row.items || []) {
      if (item.status && item.status !== "scheduled" && item.status !== "voided") {
        for (const month of item.covered_months || []) months.add(month);
      }
    }
  }
  return months;
}

/** True when the term was charged in full or by instalment, so custom months are refused. */
export function termChargedStandard(chargedLabels, line, term) {
  const labels = chargedLabels[line.fee_head_id];
  if (!labels) return false;
  const per = line.installmentsPerTerm || 1;
  if (labels.has(`Term ${term}`)) return true;
  for (let n = 1; n <= per; n++) if (labels.has(`Term ${term} - Installment ${n} of ${per}`)) return true;
  return false;
}

/** True when a one-time fee was charged, at the standard amount or custom. */
export function otherFeeCharged(chargedLabels, feeHeadId) {
  const labels = chargedLabels[feeHeadId];
  if (!labels) return false;
  return labels.has("Other Fees") || [...labels].some((label) => label.startsWith(CUSTOM_OTHER_PREFIX));
}

/** Months already charged for a monthly fee head. A cancelled charge frees its month. */
export function chargedMonthlyMonths(dues, feeHeadId) {
  const months = new Set();
  for (const row of dues || []) {
    if (row.fee_head_id !== feeHeadId) continue;
    for (const item of row.items || []) {
      if (!item.status || item.status === "scheduled" || item.status === "voided") continue;
      if (!String(item.period_label || "").startsWith(MONTH_PREFIX)) continue;
      for (const month of item.covered_months || []) months.add(month);
    }
  }
  return months;
}

/** Picked months of monthly fees as the `selected_months` of the payment request. */
export function selectedMonthsPayload(monthSelections) {
  const out = [];
  for (const [feeHeadId, months] of Object.entries(monthSelections || {})) {
    for (const month of [...months].sort()) out.push({ fee_head_id: feeHeadId, month });
  }
  return out;
}

/** Suggested amount for the months picked out of a term. */
export function proRataPaise(termAmount, picked, monthsInTerm) {
  if (!picked || !monthsInTerm) return 0;
  return Math.round((termAmount * picked) / monthsInTerm);
}

/** One custom entry of the dialog as a `custom_lines` item of the payment request. */
export function customLinePayload(feeHeadId, term, entry) {
  const payload = { fee_head_id: feeHeadId };
  if (term) {
    payload.term = term;
    payload.months = [...(entry.months || [])].sort();
  }
  payload.amount_paise = Math.round(parseFloat(entry.amount) * 100);
  payload.reason_code = entry.reason || "";
  const note = (entry.note || "").trim();
  if (note) payload.note = note;
  const items = Object.entries(entry.items || {}).filter(([, qty]) => Number(qty) > 0).map(([itemId, qty]) => ({ item_id: itemId, qty: Number(qty) }));
  if (items.length) payload.items = items;
  payload.save_to_plan = Boolean(entry.save);
  return payload;
}

const monthName = (value, language) =>
  new Intl.DateTimeFormat(language === "ar" ? "ar" : "en", { month: "short", timeZone: "UTC" }).format(new Date(`${value}-01T00:00:00Z`));

/** Months as "Oct–Dec 2025" when consecutive, else a list. */
export function monthsText(months, language = "en") {
  const ordered = [...new Set(months)].sort();
  const label = (m) => `${monthName(m, language)} ${m.slice(0, 4)}`;
  if (ordered.length <= 1) return ordered.map(label).join("");
  const following = (m) => {
    const year = Number(m.slice(0, 4));
    const month = Number(m.slice(5, 7));
    return `${year + Math.floor(month / 12)}-${String((month % 12) + 1).padStart(2, "0")}`;
  };
  const consecutive = ordered.every((m, i) => i === 0 || following(ordered[i - 1]) === m);
  if (!consecutive) return ordered.map(label).join(", ");
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  return first.slice(0, 4) === last.slice(0, 4)
    ? `${monthName(first, language)}–${label(last)}`
    : `${label(first)} – ${label(last)}`;
}

/** Readable invoice period; the stored label keeps months and the custom-charge suffix. */
export function displayPeriodLabel(label, language = "en") {
  const parts = [];
  for (let part of String(label || "").split(" + ")) {
    if (part.includes(MONTHS_MARKER)) {
      const [term, months] = part.split(MONTHS_MARKER);
      part = `${term} (${monthsText(months.split(",").map((m) => m.trim()).filter(Boolean), language)})`;
    } else if (part.startsWith(CUSTOM_OTHER_PREFIX)) {
      part = "Other Fees (custom)";
    } else if (part.startsWith(PURCHASE_PREFIX)) {
      part = "Other Fees";
    } else if (part.startsWith(MONTH_PREFIX) && /^\d{4}-\d{2}$/.test(part.slice(MONTH_PREFIX.length))) {
      part = monthsText([part.slice(MONTH_PREFIX.length)], language);
    }
    if (!parts.includes(part)) parts.push(part);
  }
  return parts.join(" + ");
}

function randomKey() {
  return globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

/**
 * Idempotency-Key for a Student Fee Payment submission. Sending the same
 * payload again (a retry after a timeout or a lost response) reuses the key, so
 * the server returns the original invoice. A changed payload is a new request
 * and gets a new key; the server refuses it if those fees are already charged.
 * `memo` is a ref's `current` object that keeps the last payload and key.
 */
export function submissionKey(memo, payload, makeKey = randomKey) {
  const signature = JSON.stringify(payload);
  if (memo.signature !== signature) {
    memo.signature = signature;
    memo.key = makeKey();
  }
  return memo.key;
}
